import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Determine API URL based on current environment
// On production (taptile.app), use same origin to avoid CORS issues
const getApiUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    // Explicitly check for taptile production domain
    const isTaptileProduction = hostname === 'taptile.app' || 
                                 hostname.endsWith('.taptile.app');
    
    if (isTaptileProduction) {
      return window.location.origin + '/api';
    }
  }
  
  return (envUrl || '') + '/api';
};

const API_URL = getApiUrl();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Initial token: prefer per-tab sessionStorage (used by impersonation tabs)
  // so the original tab keeps its super-admin token in localStorage.
  const [token, setToken] = useState(sessionStorage.getItem('token') || localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    const response = await axios.post(`${API_URL}/auth/login`, { 
      email, 
      password,
      remember_me: rememberMe 
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    
    // Track login for analytics
    try {
      await axios.post(`${API_URL}/analytics/track`, 
        { event_type: 'login', metadata: { method: 'email', remember_me: rememberMe } },
        { headers: { Authorization: `Bearer ${newToken}` } }
      );
    } catch (error) {
      console.error('Failed to track login:', error);
    }
    
    return userData;
  };

  const signup = async (email, password, name, organizationName = null, phone = null, role = 'user', customer_id = null) => {
    const response = await axios.post(`${API_URL}/auth/signup`, {
      email,
      password,
      name,
      role,
      customer_id,
      organization_name: organizationName,
      phone
    });
    
    // Auto-login after successful signup
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    // Clear both stores so logout works from any tab
    localStorage.removeItem('token');
    localStorage.removeItem('impersonating');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('impersonating');
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    loading,
    token,
    login,
    signup,
    logout,
    setUser,
    setToken,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'super_admin',
    isAdmin: user?.role === 'admin',
    isUser: user?.role === 'user'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};